// Workforce Tracker — compact, searchable summary of C&I delivered man-hours vs
// each client's cycle mandate. Route stays /commercial/performance; the component
// is still exported as before (CITeamPerformance) to avoid touching App.js.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Activity, RefreshCw, Users, Receipt, ChevronDown, ChevronRight, Clock, Search, Sun, BadgeCheck,
} from 'lucide-react';

const API = process.env.REACT_APP_API_BASE_URL;

// ─── iNOVAA dark tokens ───────────────────────────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border:  'var(--nd-w06)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx40)',
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .wt-wrap { font-family: 'DM Sans', sans-serif; color: var(--nd-text); }
  .wt-input {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-text); font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; padding: 7px 12px; outline: none;
  }
  .wt-input:focus { border-color: rgba(96,165,250,0.5); }
  .wt-search {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-text); font-family: 'DM Sans', sans-serif;
    font-size: 13px; padding: 8px 12px 8px 34px; outline: none; width: 240px;
  }
  .wt-search:focus { border-color: rgba(96,165,250,0.5); }
  .wt-btn {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-tx70); font-weight: 700;
    font-size: 12px; padding: 8px 14px; cursor: pointer; display: inline-flex;
    align-items: center; gap: 7px;
  }
  .wt-btn:hover { background: var(--nd-w10); color: var(--nd-text-strong); }
  .wt-table { width: 100%; border-collapse: collapse; }
  .wt-table th {
    text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.12em;
    color: var(--nd-tx35); text-transform: uppercase; padding: 10px 14px;
    border-bottom: 1px solid var(--nd-w08); white-space: nowrap; position: sticky; top: 0;
    background: var(--nd-surface);
  }
  .wt-table td { font-size: 13px; color: var(--nd-text); padding: 9px 14px; border-bottom: 1px solid var(--nd-w04); }
  .wt-row { cursor: pointer; }
  .wt-row:hover td { background: var(--nd-w03); }
  .wt-track { height: 6px; border-radius: 6px; background: var(--nd-w08); overflow: hidden; min-width: 90px; }
  .wt-fill { height: 100%; border-radius: 6px; }
  .wt-badge { font-family: 'DM Mono', monospace; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 20px; }
  .wt-chip { font-family: 'DM Mono', monospace; font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 6px; color: #22D3A5; background: rgba(34,211,165,0.12); border: 1px solid rgba(34,211,165,0.25); }
  .wt-stat { background: var(--nd-surface); border: 1px solid var(--nd-w06); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 140px; }
  .wt-mtable { width: 100%; border-collapse: collapse; }
  .wt-mtable th { text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.12em; color: var(--nd-tx35); text-transform: uppercase; padding: 7px 12px; }
  .wt-mtable td { font-size: 12px; padding: 7px 12px; border-top: 1px solid var(--nd-w04); }
  .wt-tag { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; white-space: nowrap; }
`;

const todayStr = () => new Date().toISOString().split('T')[0];
const monthStartStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Sites above this size earn FO commission — same rule as payroll and Mandate Config.
const COMMISSION_BASE_KW = 100;

const Stat = ({ label, value, color }) => (
  <div className="wt-stat" style={{ borderTop: `2px solid ${color}` }}>
    <div style={{ fontSize: 24, fontWeight: 800, color, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.14em', marginTop: 6, fontWeight: 700 }}>{label}</div>
  </div>
);

const Bar = ({ label, delivered, target, color }) => {
  const pct = target > 0 ? Math.min(100, Math.round((delivered / target) * 100)) : 0;
  const met = target > 0 && delivered >= target;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{label}</span>
        <span style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Mono', monospace" }}>
          {delivered}h / {target}h {target > 0 ? `· ${pct}%` : ''}
        </span>
      </div>
      <div className="wt-track" style={{ minWidth: 0 }}>
        <div className="wt-fill" style={{ width: `${pct}%`, background: met ? '#22D3A5' : color }} />
      </div>
    </div>
  );
};

const TeamPerformance = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setExpanded(new Set());
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const res = await axios.get(`${API}/ci-performance/performance?${params.toString()}`);
      setRows(res.data?.data || []);
    } catch (err) {
      console.error('Performance fetch error:', err);
      setError('Failed to load workforce data.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Summary stats over the full (unfiltered) result set.
  const summary = useMemo(() => {
    const cyclesDue = rows.reduce((s, r) => s + (r.cycles_completed || 0), 0);
    const active = rows.filter((r) => (r.days_worked || 0) > 0).length;
    const delivered = rows.reduce((s, r) => s + (r.delivered?.total || 0), 0);
    return { clients: rows.length, cyclesDue, active, delivered };
  }, [rows]);

  // Filter by search, sort active/billing-relevant clients to the top.
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = s ? rows.filter((r) => (r.client_name || '').toLowerCase().includes(s)) : rows.slice();
    return list.sort((a, b) =>
      (b.cycles_completed - a.cycles_completed) ||
      (b.delivered.total - a.delivered.total) ||
      a.client_name.localeCompare(b.client_name)
    );
  }, [rows, search]);

  return (
    <div className="wt-wrap" style={{ background: T.bg, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '18px 22px', marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Activity size={20} color="#60A5FA" />
            </div>
            <div>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 3 }}>iNOVAA · C&I</div>
              <div style={{ fontSize: 21, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                Workforce Tracker
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative' }}>
              <Search size={15} color="var(--nd-tx35)" style={{ position: 'absolute', left: 11, top: 9 }} />
              <input className="wt-search" placeholder="Search client…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <input type="date" className="wt-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} title="From" />
            <input type="date" className="wt-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} title="To" />
            <button className="wt-btn" onClick={fetchData} title="Refresh"><RefreshCw size={14} /></button>
          </div>
        </div>

        {/* Summary tiles */}
        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <Stat label="MANDATED CLIENTS" value={loading ? '—' : summary.clients} color="var(--nd-tx45)" />
          <Stat label="ACTIVE IN RANGE" value={loading ? '—' : summary.active} color="#60A5FA" />
          <Stat label="CYCLES DUE / BILLING" value={loading ? '—' : summary.cyclesDue} color="#22D3A5" />
          <Stat label="DELIVERED HOURS" value={loading ? '—' : `${summary.delivered}h`} color="#F59E0B" />
        </div>
      </div>

      {/* Table */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#F87171', fontSize: 13 }}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.muted, fontSize: 13 }}>
            No mandated clients yet. Set mandates on the config page first.
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: T.muted, fontSize: 13 }}>No client matches “{search}”.</div>
        ) : (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <table className="wt-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Client</th>
                  <th style={{ textAlign: 'center' }}>Mandate</th>
                  <th style={{ textAlign: 'center' }}>Delivered</th>
                  <th>Next cycle</th>
                  <th style={{ textAlign: 'center' }}>Cycles due</th>
                  <th style={{ textAlign: 'center' }}>Days</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const open = expanded.has(r.client_id);
                  const due = r.cycles_completed > 0;
                  return (
                    <React.Fragment key={r.client_id}>
                      <tr className="wt-row" onClick={() => toggle(r.client_id)}>
                        <td style={{ color: 'var(--nd-tx45)' }}>{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                        <td style={{ minWidth: 170 }}>
                          <div style={{ fontWeight: 700 }}>{r.client_name}</div>
                          <div style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Mono', monospace" }}>{r.contract_type}</div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
                            {/* Actual Sunday work in the range wins over the Mandate Config setting: if anyone
                                worked a Sunday, show how many; otherwise show the client's setting. */}
                            <span className="wt-tag"
                              title={r.sundays_worked > 0
                                ? `Attendance shows work on ${r.sundays_worked} Sunday${r.sundays_worked === 1 ? '' : 's'} in this range.`
                                : `${r.sunday_working ? 'Sunday is a working day' : 'Sunday off'} for this client (set on Mandate Config). No Sunday work in this range.`}
                              style={r.sundays_worked > 0 || r.sunday_working
                                ? { color: '#FCD34D', background: 'rgba(245,158,11,0.12)' }
                                : { color: 'var(--nd-tx40)', background: 'var(--nd-w05)' }}>
                              <Sun size={11} />
                              {r.sundays_worked > 0
                                ? `${r.sundays_worked} Sunday${r.sundays_worked === 1 ? '' : 's'} worked`
                                : r.sunday_working ? 'Works Sundays' : 'Sunday off'}
                            </span>
                            <span className="wt-tag"
                              title={`Commission is ${r.commission_enabled ? 'ON' : 'OFF'} for this client (Mandate Config).${r.sites?.count ? ` ${r.sites.eligible} of ${r.sites.count} sites over ${COMMISSION_BASE_KW} kW · largest ${Math.round(r.sites.max_kw)} kW.` : ' No site sizes recorded.'}`}
                              style={r.commission_enabled && r.commission_eligible
                                ? { color: '#22D3A5', background: 'rgba(34,211,165,0.12)' }
                                : { color: 'var(--nd-tx40)', background: 'var(--nd-w05)' }}>
                              <BadgeCheck size={11} />
                              {!r.commission_enabled ? 'Commission off'
                                : r.commission_eligible ? `Commission on · ${Math.round(r.sites.max_kw)} kW`
                                : `Commission on · no site > ${COMMISSION_BASE_KW} kW`}
                            </span>
                          </div>
                          {r.incomplete_punches > 0 && (
                            <div style={{ fontSize: 10, color: '#F59E0B', fontWeight: 700, marginTop: 2 }} title="Member-days missing a clock-in/out punch (counted at 8h, flagged for fixing)">
                              ⚠ {r.incomplete_punches} incomplete punch{r.incomplete_punches === 1 ? '' : 'es'}
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>{r.mandate.total}h</td>
                        <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>
                          <div style={{ color: r.delivered.total > 0 ? 'var(--nd-text)' : 'var(--nd-tx30)' }}>{r.delivered.total}h</div>
                          <div style={{ fontSize: 9, color: T.muted }} title="Actual clocked hours (transparency)">act {r.actual.total}h</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="wt-track"><div className="wt-fill" style={{ width: `${r.progress_pct}%`, background: 'linear-gradient(90deg,#3B82F6,#22D3A5)' }} /></div>
                            <span style={{ fontSize: 10, color: T.muted, fontFamily: "'DM Mono', monospace" }}>{r.progress_pct}%</span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className="wt-badge" style={due
                            ? { color: '#22D3A5', background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.3)' }
                            : { color: 'var(--nd-tx40)', background: 'var(--nd-w05)', border: '1px solid var(--nd-w10)' }}>
                            {r.cycles_completed}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: T.muted }}>{r.days_worked}</td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={7} style={{ padding: '14px 20px 18px 46px', background: 'var(--nd-w02)' }}>
                            <Bar label="Supervisor (team lead)" delivered={r.delivered.supervisor} target={r.mandate.supervisor} color="#F59E0B" />
                            <Bar label="FO / field" delivered={r.delivered.fo} target={r.mandate.fo_target} color="#60A5FA" />

                            {r.billing_due_dates.length > 0 && (
                              <div style={{ margin: '10px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Receipt size={13} color="#22D3A5" />
                                <span style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: '0.06em' }}>BILLING DUE:</span>
                                {r.billing_due_dates.map((d) => <span key={d} className="wt-chip">{d}</span>)}
                              </div>
                            )}

                            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Users size={13} color="var(--nd-tx45)" />
                              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--nd-tx40)' }}>
                                MEMBERS PRESENT ({r.members.length})
                              </span>
                            </div>
                            {r.members.length === 0 ? (
                              <div style={{ padding: '8px 0', color: T.muted, fontSize: 12 }}>No attributable attendance in this range.</div>
                            ) : (
                              <table className="wt-mtable">
                                <thead>
                                  <tr><th>Member</th><th>Role</th><th style={{ textAlign: 'center' }}>Days</th><th style={{ textAlign: 'center' }}>Assumed</th><th style={{ textAlign: 'center' }}>Actual</th><th style={{ textAlign: 'center' }}>Incomplete</th></tr>
                                </thead>
                                <tbody>
                                  {r.members.map((m, i) => (
                                    <tr key={i}>
                                      <td style={{ fontWeight: 700 }}>{m.name}</td>
                                      <td>
                                        <span className="wt-badge" style={m.role === 'Supervisor'
                                          ? { color: '#F59E0B', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)' }
                                          : { color: '#60A5FA', background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)' }}>
                                          {m.role}
                                        </span>
                                      </td>
                                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>{m.days}</td>
                                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: 'var(--nd-tx70)' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={12} />{m.hours}h</span>
                                      </td>
                                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace", color: T.muted }} title="Actual clocked hours">{m.actual_hours}h</td>
                                      <td style={{ textAlign: 'center', fontFamily: "'DM Mono', monospace" }} title="Days missing a clock-in/out punch">
                                        {m.incomplete_days > 0 ? <span style={{ color: '#F59E0B', fontWeight: 700 }}>⚠ {m.incomplete_days}</span> : <span style={{ color: 'var(--nd-tx25)' }}>—</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPerformance;
